import { CompanyType, ContentTypeIds, RouteName } from '@/constants';
import type { ISlugItem, IUrlItem } from '@/interfaces';
import { replacePath } from '@/utils';

/**
 * Check if a slug item is valid or not, this is usually used for Preview mode since in Preview mode ths slug (required field) can be empty
 * @param item ISlugItem - { slug: '', type: '' }
 * @param type ContentTypeIds
 * @returns boolean
 * @example
 * checkInvalidISlugItem({ slug: 'news' }, 'tag') => true
 * checkInvalidISlugItem({ slug: '' }, 'article') => true
 * checkInvalidISlugItem({ slug: '', articleType: 'news' }, 'article') => true
 * checkInvalidISlugItem({ slug: 'article-1', articleType: 'news' }, 'article') => false
 */
const checkInvalidISlugItem = (item: ISlugItem, type: ContentTypeIds) => {
  switch (type) {
    case ContentTypeIds.tag:
      return !item.type;
    case ContentTypeIds.article:
      return !item.articleType || !item.slug;
    case ContentTypeIds.landingPage:
    case ContentTypeIds.carrier:
    case ContentTypeIds.companyDirectory:
    case ContentTypeIds.market:
      return !item.slug;
    default:
      return false;
  }
};

/**
 * Generate full slug from a slug item
 * @param item ISlugItem - { slug: '', type: '' }
 * @param type ContentTypeIds
 * @returns string
 * @example
 * generateFullSlugFromItem({ slug: '', type: 'news' }, 'tag') => '/news'
 * generateFullSlugFromItem({ slug: 'article-1', articleType: 'news' }, 'article') => '/news/article-1'
 */
const generateFullSlugFromItem = (item: ISlugItem, type: ContentTypeIds) => {
  switch (type) {
    case ContentTypeIds.tag: {
      if (!item.slug) {
        return replacePath(RouteName.ArticleSummary, [item.type]);
      }

      return replacePath(RouteName.Article, [item.type, item.slug]);
    }
    case ContentTypeIds.article:
      return replacePath(RouteName.Article, [item.articleType, item.slug]);
    case ContentTypeIds.landingPage:
      if (item.ampPageType) {
        return replacePath(RouteName.Article, [item.ampPageType, item.slug]);
      }

      return replacePath(RouteName.ArticleSummary, [item.slug]);
    case ContentTypeIds.companyDirectory:
      return replacePath(RouteName.Carriers, [item.slug]);
    case ContentTypeIds.carrier:
      return replacePath(RouteName.IndividualCarrier, [CompanyType.carriers, item.slug]);
    case ContentTypeIds.market:
      return replacePath(RouteName.Market, [item.slug]);
    default:
      return '';
  }
};

export const convertToSitemapItems = (items: ISlugItem[] | null, type: ContentTypeIds, baseUrl: string) => {
  if (!items) return [];

  return items.reduce<IUrlItem[]>((previousItems, currentItem) => {
    if (checkInvalidISlugItem(currentItem, type)) {
      return previousItems;
    }

    const href = generateFullSlugFromItem(currentItem, type);

    return [
      ...previousItems,
      {
        location: baseUrl + href,
        priority: '0.9',
      },
    ];
  }, []);
};
